import React, { useState, useEffect, useMemo, useRef } from "react";
import styled from "styled-components";
import bibleData from "../../data/bible.json";

// 성경 권 약어 -> 전체 이름 매핑
const bookNames: { [key: string]: string } = {
  창: "창세기",
  출: "출애굽기",
  레: "레위기",
  민: "민수기",
  신: "신명기",
  수: "여호수아",
  삿: "사사기",
  룻: "룻기",
  삼상: "사무엘상",
  삼하: "사무엘하",
  왕상: "열왕기상",
  왕하: "열왕기하",
  대상: "역대상",
  대하: "역대하",
  스: "에스라",
  느: "느헤미야",
  에: "에스더",
  욥: "욥기",
  시: "시편",
  잠: "잠언",
  전: "전도서",
  아: "아가",
  사: "이사야",
  렘: "예레미야",
  애: "예레미야애가",
  겔: "에스겔",
  단: "다니엘",
  호: "호세아",
  욜: "요엘",
  암: "아모스",
  옵: "오바댜",
  욘: "요나",
  미: "미가",
  나: "나훔",
  합: "하박국",
  습: "스바냐",
  학: "학개",
  슥: "스가랴",
  말: "말라기",
  마: "마태복음",
  막: "마가복음",
  눅: "누가복음",
  요: "요한복음",
  행: "사도행전",
  롬: "로마서",
  고전: "고린도전서",
  고후: "고린도후서",
  갈: "갈라디아서",
  엡: "에베소서",
  빌: "빌립보서",
  골: "골로새서",
  살전: "데살로니가전서",
  살후: "데살로니가후서",
  딤전: "디모데전서",
  딤후: "디모데후서",
  딛: "디도서",
  몬: "빌레몬서",
  히: "히브리서",
  약: "야고보서",
  벧전: "베드로전서",
  벧후: "베드로후서",
  요일: "요한일서",
  요이: "요한이서",
  요삼: "요한삼서",
  유: "유다서",
  계: "요한계시록",
};

// 성경 권 순서
const bookOrder = [
  "창", "출", "레", "민", "신", "수", "삿", "룻", "삼상", "삼하",
  "왕상", "왕하", "대상", "대하", "스", "느", "에", "욥", "시", "잠",
  "전", "아", "사", "렘", "애", "겔", "단", "호", "욜", "암",
  "옵", "욘", "미", "나", "합", "습", "학", "슥", "말",
  "마", "막", "눅", "요", "행", "롬", "고전", "고후", "갈", "엡",
  "빌", "골", "살전", "살후", "딤전", "딤후", "딛", "몬", "히", "약",
  "벧전", "벧후", "요일", "요이", "요삼", "유", "계"
];

interface ScriptureSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

interface ScriptureRange {
  startBook: string;
  startChapter: string;
  startVerse: string;
  endBook: string;
  endChapter: string;
  endVerse: string;
}

const bible = bibleData as { [book: string]: { [chapter: string]: { [verse: string]: string } } };

const ScriptureSelector: React.FC<ScriptureSelectorProps> = ({ value, onChange }) => {
  const [range, setRange] = useState<ScriptureRange>({
    startBook: "",
    startChapter: "",
    startVerse: "",
    endBook: "",
    endChapter: "",
    endVerse: "",
  });

  // 초기 파싱 중인지 여부 (onChange 호출 방지)
  const isInitializing = useRef(!!value); // value가 있을 때만 초기화 중
  const prevValue = useRef(value);
  
  // onChange를 ref로 관리하여 무한 루프 방지
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // 전체 이름 -> 약어 역방향 매핑
  const bookNameToAbbr = useMemo(() => {
    const map: { [key: string]: string } = {};
    for (const [abbr, name] of Object.entries(bookNames)) {
      map[name] = abbr;
    }
    return map;
  }, []);

  // 컴포넌트 마운트 후 초기화 완료
  useEffect(() => {
    // 초기 value가 없으면 바로 초기화 완료
    if (!value) {
      isInitializing.current = false;
    }
  }, []);

  // 기존 value 파싱 (예: "창세기 1:1-10" 또는 "창세기 1:1 - 2:5" 또는 "창세기 1:1 - 출애굽기 2:5")
  useEffect(() => {
    // value가 외부에서 변경된 경우에만 파싱 (사용자 입력으로 인한 변경은 무시)
    if (prevValue.current === value && !isInitializing.current) return;
    prevValue.current = value;
    
    if (!value) {
      setRange({
        startBook: "",
        startChapter: "",
        startVerse: "",
        endBook: "",
        endChapter: "",
        endVerse: "",
      });
      isInitializing.current = false;
      return;
    }
    
    isInitializing.current = true;

    try {
      // " - "로 분리 시도 (다른 장/권 범위)
      const dashParts = value.split(/\s+-\s+/);
      
      if (dashParts.length === 1) {
        // 단일 구절 또는 같은 장 범위: "창세기 1:1" 또는 "창세기 1:1-10"
        const match = value.match(/^(.+?)\s*(\d+):(\d+)(?:-(\d+))?$/);
        if (match) {
          const [, bookName, chapter, startV, endV] = match;
          const bookAbbr = bookNameToAbbr[bookName.trim()] || bookName.trim();
          
          setRange({
            startBook: bookAbbr,
            startChapter: chapter,
            startVerse: startV,
            endBook: bookAbbr,
            endChapter: chapter,
            endVerse: endV || startV,
          });
        }
      } else if (dashParts.length === 2) {
        // 시작 파싱
        const startMatch = dashParts[0].match(/^(.+?)\s*(\d+):(\d+)$/);
        if (!startMatch) {
          setTimeout(() => { isInitializing.current = false; }, 0);
          return;
        }

        const [, startBookName, startCh, startV] = startMatch;
        const startBookAbbr = bookNameToAbbr[startBookName.trim()] || startBookName.trim();

        // 끝 파싱
        const endPart = dashParts[1].trim();
        let endBookAbbr = startBookAbbr;
        let endCh = startCh;
        let endV = "";

        // "5" (같은 장의 절만) - 이 경우는 " - "로 분리되지 않음
        // "2:5" (같은 권의 다른 장)
        if (/^(\d+):(\d+)$/.test(endPart)) {
          const match = endPart.match(/^(\d+):(\d+)$/);
          if (match) {
            endCh = match[1];
            endV = match[2];
          }
        }
        // "출애굽기 2:5" (다른 권)
        else {
          const endMatch = endPart.match(/^(.+?)\s*(\d+):(\d+)$/);
          if (endMatch) {
            const [, endBookName, ch, v] = endMatch;
            endBookAbbr = bookNameToAbbr[endBookName.trim()] || endBookName.trim();
            endCh = ch;
            endV = v;
          }
        }

        setRange({
          startBook: startBookAbbr,
          startChapter: startCh,
          startVerse: startV,
          endBook: endBookAbbr,
          endChapter: endCh,
          endVerse: endV,
        });
      }
    } catch (error) {
      console.error("Error parsing scripture value:", error);
    }

    setTimeout(() => { isInitializing.current = false; }, 0);
  }, [value, bookNameToAbbr]);

  // 권 목록
  const books = useMemo(() => {
    return bookOrder.filter(book => bible[book]);
  }, []);

  // 시작 권에 따른 장 목록
  const startChapters = useMemo(() => {
    if (!range.startBook || !bible[range.startBook]) return [];
    return Object.keys(bible[range.startBook]).sort((a, b) => Number(a) - Number(b));
  }, [range.startBook]);

  // 시작 장에 따른 절 목록
  const startVerses = useMemo(() => {
    if (!range.startBook || !range.startChapter) return [];
    const chapter = bible[range.startBook]?.[range.startChapter];
    if (!chapter) return [];
    return Object.keys(chapter).sort((a, b) => Number(a) - Number(b));
  }, [range.startBook, range.startChapter]);

  // 끝 권에 따른 장 목록
  const endChapters = useMemo(() => {
    if (!range.endBook || !bible[range.endBook]) return [];
    return Object.keys(bible[range.endBook]).sort((a, b) => Number(a) - Number(b));
  }, [range.endBook]);

  // 끝 장에 따른 절 목록
  const endVerses = useMemo(() => {
    if (!range.endBook || !range.endChapter) return [];
    const chapter = bible[range.endBook]?.[range.endChapter];
    if (!chapter) return [];
    return Object.keys(chapter).sort((a, b) => Number(a) - Number(b));
  }, [range.endBook, range.endChapter]);

  // 값 변경 시 scripture 문자열 생성
  useEffect(() => {
    // 초기 파싱 중에는 onChange 호출하지 않음
    if (isInitializing.current) {
      return;
    }

    const { startBook, startChapter, startVerse, endBook, endChapter, endVerse } = range;
    
    if (!startBook || !startChapter || !startVerse) {
      return;
    }

    let scriptureText = "";
    const startBookName = bookNames[startBook] || startBook;
    const endBookName = bookNames[endBook] || endBook;

    if (endBook && endChapter && endVerse) {
      // 범위가 있는 경우
      if (startBook === endBook) {
        if (startChapter === endChapter) {
          // 같은 권, 같은 장
          if (startVerse === endVerse) {
            scriptureText = `${startBookName} ${startChapter}:${startVerse}`;
          } else {
            scriptureText = `${startBookName} ${startChapter}:${startVerse}-${endVerse}`;
          }
        } else {
          // 같은 권, 다른 장
          scriptureText = `${startBookName} ${startChapter}:${startVerse} - ${endChapter}:${endVerse}`;
        }
      } else {
        // 다른 권
        scriptureText = `${startBookName} ${startChapter}:${startVerse} - ${endBookName} ${endChapter}:${endVerse}`;
      }
    } else {
      // 단일 구절
      scriptureText = `${startBookName} ${startChapter}:${startVerse}`;
    }

    prevValue.current = scriptureText;
    onChangeRef.current(scriptureText);
  }, [range]); // onChange를 dependency에서 제거

  const handleChange = (field: keyof ScriptureRange, value: string) => {
    setRange(prev => {
      const newRange = { ...prev, [field]: value };
      
      // 시작 권이 바뀌면 시작 장/절 초기화
      if (field === "startBook") {
        newRange.startChapter = "";
        newRange.startVerse = "";
      }
      // 시작 장이 바뀌면 시작 절 초기화
      if (field === "startChapter") {
        newRange.startVerse = "";
      }
      // 끝 권이 바뀌면 끝 장/절 초기화
      if (field === "endBook") {
        newRange.endChapter = "";
        newRange.endVerse = "";
      }
      // 끝 장이 바뀌면 끝 절 초기화
      if (field === "endChapter") {
        newRange.endVerse = "";
      }
      
      return newRange;
    });
  };

  // 시작 구절 선택 시 자동으로 끝 구절도 같은 값으로 설정
  const handleStartVerseChange = (verse: string) => {
    setRange(prev => ({
      ...prev,
      startVerse: verse,
      endBook: prev.endBook || prev.startBook,
      endChapter: prev.endChapter || prev.startChapter,
      endVerse: prev.endVerse || verse,
    }));
  };

  return (
    <Container>
      <RangeSection>
        <RangeLabel>시작</RangeLabel>
        <SelectGroup>
          <Select
            value={range.startBook}
            onChange={(e) => handleChange("startBook", e.target.value)}
          >
            <option value="">권 선택</option>
            {books.map(book => (
              <option key={book} value={book}>{bookNames[book]}</option>
            ))}
          </Select>
          <Select
            value={range.startChapter}
            onChange={(e) => handleChange("startChapter", e.target.value)}
            disabled={!range.startBook}
          >
            <option value="">장</option>
            {startChapters.map(chapter => (
              <option key={chapter} value={chapter}>{chapter}장</option>
            ))}
          </Select>
          <Select
            value={range.startVerse}
            onChange={(e) => handleStartVerseChange(e.target.value)}
            disabled={!range.startChapter}
          >
            <option value="">절</option>
            {startVerses.map(verse => (
              <option key={verse} value={verse}>{verse}절</option>
            ))}
          </Select>
        </SelectGroup>
      </RangeSection>

      <Divider>~</Divider>

      <RangeSection>
        <RangeLabel>끝</RangeLabel>
        <SelectGroup>
          <Select
            value={range.endBook}
            onChange={(e) => handleChange("endBook", e.target.value)}
            disabled={!range.startVerse}
          >
            <option value="">권 선택</option>
            {books.map(book => (
              <option key={book} value={book}>{bookNames[book]}</option>
            ))}
          </Select>
          <Select
            value={range.endChapter}
            onChange={(e) => handleChange("endChapter", e.target.value)}
            disabled={!range.endBook}
          >
            <option value="">장</option>
            {endChapters.map(chapter => (
              <option key={chapter} value={chapter}>{chapter}장</option>
            ))}
          </Select>
          <Select
            value={range.endVerse}
            onChange={(e) => handleChange("endVerse", e.target.value)}
            disabled={!range.endChapter}
          >
            <option value="">절</option>
            {endVerses.map(verse => (
              <option key={verse} value={verse}>{verse}절</option>
            ))}
          </Select>
        </SelectGroup>
      </RangeSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const RangeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RangeLabel = styled.span`
  font-size: 12px;
  color: #888;
  font-weight: 600;
`;

const SelectGroup = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    flex-wrap: wrap;
  }
`;

const Select = styled.select`
  height: 44px;
  padding: 0 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 100px;
  background: white;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    border-color: #004094;
    outline: none;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
    color: #999;
  }

  @media (max-width: 480px) {
    height: 44px;
    min-width: 80px;
    padding: 0 10px;
    font-size: 13px;
  }
`;

const Divider = styled.span`
  font-size: 20px;
  color: #666;
  padding-top: 28px;

  @media (max-width: 768px) {
    padding-top: 0;
    align-self: center;
  }
`;

export default ScriptureSelector;
